import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Typography,
  Paper,
  List,
  ListItem,
  ListItemText,
  Stack,
  IconButton,
} from '@mui/material';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import { rulesManager } from '../utils/rulesManager';
import RuleModal from './RuleModal';
import ErrorSnackbar from './ErrorSnackbar';

const Rules = ({ 
  rules = [],
  onRuleApply,
  onRuleCreate,
  onRuleOrderChange,
  currentFilters,
  allTags,
  minAmount,
  maxAmount,
  minDate,
  maxDate,
}) => {
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedHistoryEntry, setSelectedHistoryEntry] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [selectedRuleId, setSelectedRuleId] = useState(null);
  const [selectedRule, setSelectedRule] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');
  const [showError, setShowError] = useState(false);

  const handleDelete = (ruleId) => {
    if (!rules) return;
    
    try {
      // Get updated rules from rulesManager
      const updatedRules = rulesManager.deleteRule(ruleId);
      // Update the rules state through the parent component
      onRuleOrderChange(updatedRules);
    } catch (error) {
      setErrorMessage(error.message || "Error deleting rule");
      setShowError(true);
    }
  };

  const handleDragEnd = (result) => {
    if (!result.destination || !rules) return;

    const items = Array.from(rules);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    onRuleOrderChange(items);
  };

  const handleCreateNew = () => {
    setSelectedHistoryEntry(null);
    setEditMode(false);
    setSelectedRuleId(null);
    setSelectedRule(null);
    setModalOpen(true);
  };

  const handleRuleClick = (rule) => {
    setSelectedHistoryEntry(null);
    setEditMode(true);
    setSelectedRuleId(rule.id);
    setSelectedRule(rule);
    setModalOpen(true);
  };

  const handleSaveRule = (rule, editRuleId = null) => {
    try {
      let updatedRules;
      
      if (editMode && editRuleId) {
        // Update existing rule
        updatedRules = rulesManager.updateRule(editRuleId, rule);
      } else {
        // Create new rule
        updatedRules = rulesManager.addRule(rule);
      }
      
      // Update the rules state through the parent component
      onRuleCreate(rule);
      onRuleOrderChange(updatedRules);
    } catch (error) {
      setErrorMessage(error.message || "Error saving rule");
      setShowError(true);
    }
  };

  const handleApplyAll = () => {
    if (rules && rules.length > 0) {
      try {
        rules.forEach(rule => onRuleApply(rule));
      } catch (error) {
        setErrorMessage(error.message || "Error applying rules");
        setShowError(true);
      }
    }
  };

  useEffect(() => {
    if (rules && rules.length > 0) {
      // Force a re-render of the rules list when rules change
      setModalOpen(false);
    }
  }, [rules]);

  return (
    <Box sx={{ mt: 4 }}>
      <ErrorSnackbar
        open={showError}
        message={errorMessage}
        onClose={() => setShowError(false)}
      />
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">
          Rules
        </Typography>
        <Stack direction="row" spacing={2}>
          <Button 
            variant="contained" 
            onClick={handleCreateNew}
          >
            Create New Rule
          </Button>
          <Button 
            variant="outlined"
            onClick={handleApplyAll}
            disabled={!rules || rules.length === 0}
          >
            Apply All Rules
          </Button>
        </Stack>
      </Box>
      <Paper sx={{ p: 2 }}>
        <DragDropContext onDragEnd={handleDragEnd}>
          <Droppable droppableId="rules" isDropDisabled={false} isCombineEnabled={false} ignoreContainerClipping={true}>
            {(provided) => (
              <List {...provided.droppableProps} ref={provided.innerRef}>
                {rules && rules.map((rule, index) => (
                  <Draggable key={rule.id.toString()} draggableId={rule.id} index={index}>
                    {(provided) => (
                      <ListItem
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        {...provided.dragHandleProps}
                        divider
                        onClick={() => handleRuleClick(rule)}
                        sx={{ 
                          cursor: 'pointer',
                          '&:hover': {
                            backgroundColor: 'rgba(0, 0, 0, 0.04)',
                          },
                        }}
                        secondaryAction={
                          <Stack direction="row" spacing={1}>
                            <Button
                              variant="outlined"
                              onClick={(e) => {
                                e.stopPropagation(); // Prevent triggering the ListItem onClick
                                onRuleApply(rule);
                              }}
                              sx={{ mr: 1 }}
                            >
                              Apply Rule
                            </Button>
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation(); // Prevent triggering the ListItem onClick
                                handleDelete(rule.id);
                              }}
                            >
                              ✕
                            </IconButton>
                          </Stack>
                        }
                      >
                        <ListItemText
                          primary={`${index + 1}. ${rule.name}`}
                          secondary={`${rule.actions.length} action(s)`}
                        />
                      </ListItem>
                    )}
                  </Draggable>
                ))}
                {provided.placeholder}
              </List>
            )}
          </Droppable>
        </DragDropContext>
      </Paper>

      <RuleModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        initialFilters={
          editMode && selectedRule 
            ? selectedRule.filters 
            : selectedHistoryEntry 
              ? selectedHistoryEntry.filters 
              : currentFilters
        }
        initialActions={
          editMode && selectedRule 
            ? selectedRule.actions 
            : selectedHistoryEntry 
              ? [selectedHistoryEntry.action] 
              : []
        }
        allTags={allTags}
        onSave={handleSaveRule}
        minAmount={minAmount}
        maxAmount={maxAmount}
        minDate={minDate}
        maxDate={maxDate}
        editMode={editMode}
        editRuleId={selectedRuleId}
      />
    </Box>
  );
};

export default Rules;







